import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Snackbar, Alert, AlertColor } from '@mui/material';
import { LAYOUT_CLASSES } from '@/components/ui/styles/componentClasses';
import DashboardHeader from '@/components/dashboard/layout/DashboardHeader';
import { auth } from '@/lib/picasso-firebase';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';
import { useAllAnalysesInfinite, useParsnipQueryClient, useAnalysisUsers, useDeleteAnalysis } from '@/hooks/querry_hooks/parsnip/useParsnipQuery';
import { useUploadParsnipVideo, useAnalyzePetReactions, useAnalysisStatusCheck } from '@/hooks/parsnip/useParsnipAnalysis';

import type { GalleryItem, PendingTileState } from '../types';
import {
  validateVideoFiles,
  validateAnalysisInputs,
  buildContentProgressCb,
  buildResultProgressCb,
} from '../utils';

import ParsnipLogoHeader from '../layout/ParsnipLogoHeader';
import ContentUploadZone from '../ui/ContentUploadZone';
import ResultsUploadZone from '../ui/ResultsUploadZone';
import AnalyseButton from '../ui/AnalyseButton';
import ParsnipGallery from '../gallery/ParsnipGallery';
import AnalysisDetailModal from '../modals/AnalysisDetailModal';
import PendingAnalysisModal from '../modals/PendingAnalysisModal';
import PetNameModal from '../modals/PetNameModal';

interface ParsnipPageProps {
  userEmail: string | null;
  isAdmin: boolean;
}

const PENDING_TILE_STORAGE_KEY = 'parsnip-pending-tile';

// Helper function to normalize pet name: capitalize first letter of each word and trim spaces
const normalizePetName = (name: string): string => {
  return name
    .trim()
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ParsnipPage: React.FC<ParsnipPageProps> = ({ userEmail, isAdmin }) => {
  const [contentFiles, setContentFiles] = useState<File[]>([]);
  const [resultFiles, setResultFiles] = useState<File[]>([]);
  const [contentPreviewUrl, setContentPreviewUrl] = useState<string | null>(null);
  const [resultPreviewUrls, setResultPreviewUrls] = useState<string[]>([]);
  const [petName, setPetName] = useState('');
  const [showPetNameModal, setShowPetNameModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  
  // Toast notification state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('error');

  // Gallery state
  const [selectedGalleryItem, setSelectedGalleryItem] = useState<GalleryItem | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'pet_name'>('newest');
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'failed' | 'pending'>('all');
  const [userEmailFilter, setUserEmailFilter] = useState<string>('');

  // React Query for gallery
  const analysesQuery = useAllAnalysesInfinite(sortBy, statusFilter, userEmailFilter);
  const { data: availableUsers = [] } = useAnalysisUsers(isAdmin);
  const { invalidateAnalyses, patchThumbnailInCache } = useParsnipQueryClient();

  // Mutation hooks
  const uploadVideoMutation = useUploadParsnipVideo();
  const analyzeMutation = useAnalyzePetReactions();
  const checkAnalysisStatus = useAnalysisStatusCheck();
  const deleteAnalysisMutation = useDeleteAnalysis();

  const galleryItems = useMemo<GalleryItem[]>(() => {
    if (!analysesQuery.data) return [];
    return analysesQuery.data.pages.flatMap(page => page.analyses);
  }, [analysesQuery.data]);

  const galleryLoading = analysesQuery.isLoading;

  // Keep the open modal in sync when gallery data refreshes
  useEffect(() => {
    if (!selectedGalleryItem) return;
    const updated = galleryItems.find(g => g.id === selectedGalleryItem.id);
    if (updated && updated !== selectedGalleryItem) {
      setSelectedGalleryItem(updated);
    }
  }, [galleryItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoadingMore = analysesQuery.isFetchingNextPage;
  const hasMore = analysesQuery.hasNextPage ?? false;
  const loadMore = useCallback(() => { analysesQuery.fetchNextPage(); }, [analysesQuery]);


  // Pending-analysis tile shown in gallery while job is in flight
  const [pendingTile, setPendingTile] = useState<PendingTileState | null>(null);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const fetchGallery = useCallback(async () => {
    await invalidateAnalyses();
    setPendingTile(null);
    setShowPendingModal(false);
  }, [invalidateAnalyses]);
  

  // Refs for file input elements
  const contentInputRef = useRef<HTMLInputElement>(null);
  const resultInputRef = useRef<HTMLInputElement>(null);

  const clearUploadFields = useCallback(() => {
    setContentFiles([]);
    setContentPreviewUrl(null);
    setResultFiles([]);
    setResultPreviewUrls([]);
    if (contentInputRef.current) contentInputRef.current.value = '';
    if (resultInputRef.current) resultInputRef.current.value = '';
  }, []);

  // Refs for WebSocket-cancellable polling
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingPollRef = useRef<{
    analysisId: string;
    capturedPetName: string;
    resolve: () => void;
    reject: (e: Error) => void;
  } | null>(null);

  // WebSocket subscription — resolves polling early when analysis completes
  const { subscribe, unsubscribe } = useWebSocket();
  useEffect(() => {
    const handlerId = 'parsnip-analysis-ws';
    subscribe(handlerId, async (data: any) => {
      const messageType = data?.event || data?.type;
      if (messageType !== 'parsnip_analysis_completed') return;

      const evtEmail = data?.data?.user_email;
      const evtAnalysisId = data?.data?.analysis_id;
      if (evtEmail !== userEmail) return;

      const pending = pendingPollRef.current;
      if (pending && pending.analysisId === evtAnalysisId) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        pendingPollRef.current = null;

        try {
          const statusRes = await checkAnalysisStatus(pending.analysisId);
          if (statusRes.status === 'completed') {
            clearUploadFields();
          }
        } catch {
          // ignore — polling fallback would have caught this
        }
        pending.resolve();
      }

      setPendingTile((prev) => prev && prev.stage !== 'failed' ? { ...prev, stage: 'analysing' } : prev);
      fetchGallery();
    });
    return () => unsubscribe(handlerId);
  }, [subscribe, unsubscribe, userEmail, fetchGallery, clearUploadFields]);

  // WebSocket: update thumbnail URLs in gallery items as they arrive
  useEffect(() => {
    const handlerId = 'parsnip-thumbnail-ws';
    subscribe(handlerId, (data: any) => {
      const messageType = data?.event || data?.type;
      if (messageType !== 'thumbnail_updated') return;
      const evtEmail = data?.data?.user_email;
      if (evtEmail !== userEmail) return;
      const { video_id, thumbnail_url } = data?.data ?? {};
      if (!video_id || !thumbnail_url) return;
      patchThumbnailInCache(video_id, thumbnail_url);
    });
    return () => unsubscribe(handlerId);
  }, [subscribe, unsubscribe, userEmail]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (contentPreviewUrl) {
        URL.revokeObjectURL(contentPreviewUrl);
      }
      resultPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [contentPreviewUrl, resultPreviewUrls]);

  // (Gallery is fetched automatically by React Query on mount)

  // Save pending tile to localStorage whenever it changes
  useEffect(() => {
    if (pendingTile) {
      localStorage.setItem(PENDING_TILE_STORAGE_KEY, JSON.stringify(pendingTile));
    } else {
      localStorage.removeItem(PENDING_TILE_STORAGE_KEY);
    }
  }, [pendingTile]);

  // Restore pending tile from localStorage on mount and check status
  useEffect(() => {
    const restorePendingTile = async () => {
      const stored = localStorage.getItem(PENDING_TILE_STORAGE_KEY);
      if (!stored) return;

      try {
        const pendingData: PendingTileState = JSON.parse(stored);

        // Check if the analysis is still in progress
        const statusRes = await checkAnalysisStatus(pendingData.id);

        if (statusRes.status === 'completed' || statusRes.status === 'failed') {
          // Analysis finished (completed or failed) - remove pending tile and show the tile in gallery
          localStorage.removeItem(PENDING_TILE_STORAGE_KEY);
          fetchGallery();
        } else {
          // Still pending - restore the tile with 'analysing' stage
          setPendingTile({
            ...pendingData,
            stage: 'analysing',
            contentProgress: 100,
            resultProgresses: pendingData.resultProgresses.map(() => 100),
          });
        }
      } catch (error) {
        // If we can't check status (e.g., network error, invalid ID), clear the stored tile
        console.error('Failed to restore pending tile:', error);
        localStorage.removeItem(PENDING_TILE_STORAGE_KEY);
      }
    };

    restorePendingTile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const processContentFiles = (files: File[]) => {
    setContentFiles(files);
    if (contentPreviewUrl) {
      URL.revokeObjectURL(contentPreviewUrl);
    }
    setContentPreviewUrl(URL.createObjectURL(files[0]));
  };

  const processResultFiles = (files: File[]) => {
    const newFiles = [...resultFiles, ...files];
    setResultFiles(newFiles);
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));
    setResultPreviewUrls([...resultPreviewUrls, ...newPreviewUrls]);
  };

  const handleContentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const files = Array.from(event.target.files);
    const validationError = validateVideoFiles(files);
    if (validationError) {
      setToastMessage(validationError);
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    processContentFiles(files);
  };

  const handleResultUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const files = Array.from(event.target.files);
    const validationError = validateVideoFiles(files);
    if (validationError) {
      setToastMessage(validationError);
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    const newFiles = [...resultFiles, ...files];
    if (newFiles.length > 4) {
      setToastMessage(`Maximum 4 result videos allowed. You can add ${4 - resultFiles.length} more.`);
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    processResultFiles(files);
  };

  const handleRemoveResultVideo = (index: number) => {
    URL.revokeObjectURL(resultPreviewUrls[index]);
    setResultFiles(resultFiles.filter((_, i) => i !== index));
    setResultPreviewUrls(resultPreviewUrls.filter((_, i) => i !== index));
  };

  const handleRemoveContentVideo = () => {
    if (contentPreviewUrl) {
      URL.revokeObjectURL(contentPreviewUrl);
    }
    setContentFiles([]);
    setContentPreviewUrl(null);
  };

  const performAnalysis = async (contentVideoId: string, resultVideoIds: string[]) => {
    setUploadProgress('Analyzing pet reactions with Gemini...');
    const normalizedPetName = petName ? normalizePetName(petName) : undefined;
    return await analyzeMutation.mutateAsync({
      content_video_id: contentVideoId,
      result_video_ids: resultVideoIds,
      pet_name: normalizedPetName,
    });
  };

  const handleAnalyse = () => {
    const currentUser = auth.currentUser;
    const validationError = validateAnalysisInputs(currentUser, contentFiles.length, resultFiles.length);
    if (validationError) {
      setToastMessage(validationError);
      setToastSeverity('error');
      setToastOpen(true);
      return;
    }
    setShowPetNameModal(true);
  };

  const proceedWithAnalysis = async () => {
    setShowPetNameModal(false);

    // Normalize pet name and update state
    const normalizedPetName = petName ? normalizePetName(petName) : '';
    setPetName(normalizedPetName);

    const capturedContentFile = contentFiles[0];
    const capturedResultFiles = [...resultFiles];
    clearUploadFields();

    const pendingId = crypto.randomUUID();
    
    setPendingTile({
      id: pendingId,
      petName: normalizedPetName,
      contentFileName: capturedContentFile.name,
      resultFileNames: capturedResultFiles.map((f) => f.name),
      stage: 'uploading',
      contentProgress: 0,
      resultProgresses: capturedResultFiles.map(() => 0),
    });

    setLoading(true);
    setUploadProgress('Preparing upload...');

    let analysisResponse: any = null;
    
    try {
      const newBatchId = crypto.randomUUID();

      // Upload content video first (it's usually the largest)
      setUploadProgress('Uploading content video...');
      const contentUploadResponse = await uploadVideoMutation.mutateAsync({
        file: capturedContentFile,
        videoCategory: 'content',
        onProgress: buildContentProgressCb(pendingId, setPendingTile),
        batchId: newBatchId,
      });
      const contentVideoId = contentUploadResponse.video.id;

      // Then upload all result videos in parallel
      setUploadProgress('Uploading reaction videos...');
      const resultUploadPromises = capturedResultFiles.map((file, index) =>
        uploadVideoMutation.mutateAsync({
          file,
          videoCategory: 'result',
          onProgress: buildResultProgressCb(pendingId, index, setPendingTile),
          batchId: newBatchId,
        })
      );

      const resultUploadResponses = await Promise.all(resultUploadPromises);
      const resultVideoIds = resultUploadResponses.map((r: any) => r.video.id);

      setUploadProgress('Submitting analysis job...');
      setPendingTile((prev) => prev?.id === pendingId ? { ...prev, stage: 'submitting' } : prev);
      analysisResponse = await performAnalysis(contentVideoId, resultVideoIds);

      if (analysisResponse.status === 'pending') {
        setUploadProgress('Analysing pet reactions with Gemini (this may take a minute)...');
        setPendingTile((prev) => prev?.id === pendingId ? { ...prev, stage: 'analysing' } : prev);

        const POLL_INTERVAL = 4000;
        const MAX_POLLS = 150;
        let polls = 0;

        const analysisId = analysisResponse.analysis_id;

        await new Promise<void>((resolve, reject) => {
          pendingPollRef.current = { analysisId, capturedPetName: petName, resolve, reject };

          pollIntervalRef.current = setInterval(async () => {
            polls += 1;
            try {
              const statusRes = await checkAnalysisStatus(analysisId);

              if (statusRes.status === 'completed') {
                clearInterval(pollIntervalRef.current as ReturnType<typeof setInterval>);
                pollIntervalRef.current = null;
                pendingPollRef.current = null;
                resolve();
              } else if (statusRes.status === 'failed') {
                clearInterval(pollIntervalRef.current as ReturnType<typeof setInterval>);
                pollIntervalRef.current = null;
                pendingPollRef.current = null;
                reject(new Error(statusRes.error ?? 'Analysis failed'));
              } else if (polls >= MAX_POLLS) {
                clearInterval(pollIntervalRef.current as ReturnType<typeof setInterval>);
                pollIntervalRef.current = null;
                pendingPollRef.current = null;
                reject(new Error('Analysis timed out. Please try again.'));
              }
            } catch (pollErr) {
              clearInterval(pollIntervalRef.current as ReturnType<typeof setInterval>);
              pollIntervalRef.current = null;
              pendingPollRef.current = null;
              reject(pollErr);
            }
          }, POLL_INTERVAL);
        });

      }

      setUploadProgress('');
      setPetName('');
      clearUploadFields();
      fetchGallery();
    } catch (err) {
      console.error('Analysis failed:', err);
      const errMsg = err instanceof Error ? err.message : 'Analysis failed';
      setToastMessage(errMsg);
      setToastSeverity('error');
      setToastOpen(true);
      
      // Remove pending tile immediately and refresh gallery to show the failed tile with videos
      setPendingTile((prev) => prev?.id === pendingId ? null : prev);
      setPetName('');
      clearUploadFields();
      fetchGallery();
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  return (
    <Box className={LAYOUT_CLASSES.container} sx={{ backgroundColor: '#fff' }}>
      <DashboardHeader
        isAdmin={isAdmin}
        userEmail={userEmail}
        showNotifications={false}
        showFavorites={false}
      />

      <ParsnipLogoHeader />

      {/* Upload Section */}
      <Box
        sx={{
          display: 'flex',
          gap: '110px',
          paddingY: 4,
          paddingX: '152px',
          justifyContent: 'center',
          alignItems: 'stretch',
        }}
      >
        <ContentUploadZone
          contentPreviewUrl={contentPreviewUrl}
          contentFiles={contentFiles}
          inputRef={contentInputRef}
          onUpload={handleContentUpload}
          onRemove={handleRemoveContentVideo}
        />

        <ResultsUploadZone
          resultPreviewUrls={resultPreviewUrls}
          resultFiles={resultFiles}
          inputRef={resultInputRef}
          onUpload={handleResultUpload}
          onRemoveResult={handleRemoveResultVideo}
        />
      </Box>

      <AnalyseButton
        disabled={contentFiles.length === 0 || resultFiles.length === 0}
        loading={loading}
        onClick={handleAnalyse}
      />

      {/* Upload Progress */}
      {uploadProgress && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingY: 2, gap: 1 }}>
          <Typography variant="body1" sx={{ fontFamily: 'Maven Pro', color: '#666' }}>
            {uploadProgress}
          </Typography>
        </Box>
      )}

      {/* Error Message */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Pet Name Modal */}
      {showPetNameModal && (
        <PetNameModal
          petName={petName}
          onPetNameChange={(name) => setPetName(name)}
          onClose={() => setShowPetNameModal(false)}
          onProceed={proceedWithAnalysis}
        />
      )}

      <ParsnipGallery
        galleryItems={galleryItems}
        galleryLoading={galleryLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        pendingTile={pendingTile}
        sortBy={sortBy}
        statusFilter={statusFilter}
        userEmailFilter={userEmailFilter}
        availableUsers={availableUsers}
        onSortChange={setSortBy}
        onStatusFilterChange={setStatusFilter}
        onUserEmailFilterChange={setUserEmailFilter}
        onItemClick={setSelectedGalleryItem}
        onPendingTileClick={() => setShowPendingModal(true)}
      />

      {selectedGalleryItem && (
        <AnalysisDetailModal
          item={selectedGalleryItem}
          onClose={() => setSelectedGalleryItem(null)}
          userEmail={userEmail ?? ''}
          isAdmin={isAdmin}
          onRerunSuccess={fetchGallery}
          onDelete={async (id) => {
            try {
              await deleteAnalysisMutation.mutateAsync(id);
              setToastMessage('Analysis deleted successfully');
              setToastSeverity('success');
              setToastOpen(true);
            } catch {
              setToastMessage('Failed to delete analysis');
              setToastSeverity('error');
              setToastOpen(true);
            }
          }}
        />
      )}

      {showPendingModal && pendingTile && (
        <PendingAnalysisModal
          pending={pendingTile}
          onClose={() => setShowPendingModal(false)}
        />
      )}
    </Box>
  );
};

export default ParsnipPage;
